import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import { gsap } from "gsap";
import { Container, Graphics, Sprite } from "pixi.js";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { EventsManager, GameEvents, ReelsEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { FakeScatterData } from "../../../abstracts/types/interfaces";
import { generateScaledSprite } from "../../../abstracts/utils/GraphicUtils";
import { DDD } from "../../DDD";
import { DDDFontsNames, DDDSpineNames } from "../../assetsLoader/DDDAssetsNames";
import {
    devDebugConfig,
    generalMultiplier,
    reelsConfig,
    symbolConfig,
} from "../../config/DDDConfig";
import { DDDSymbolNames } from "../../types/DDDEnums";
import DDDReel from "./DDDReel";
import { DDDScatterSymbol } from "./symbols/DDDScatterSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "./symbols/DDDSymbolsPool";

export class DDDReelsView extends AbstractView {
    private readonly currentReels: DDDReel[];
    private reelsGridSprite: Sprite;
    private reelsContainerMask: Container;
    private reelsContainer: Container;
    private logoSprite: Sprite;
    private lowerReelsMask: Sprite;
    private upperReelsMask: Sprite;
    private respinFrame: AbstractSpine;
    private isRespinFrame: boolean = false;
    private generalMultiplier: AbstractBitmapText;

    public fakeScatters: DDDScatterSymbol[];

    constructor() {
        super(`DDDReelsView`);

        this.currentReels = [];
    }

    protected override initEvents() {
        super.initEvents();

        EventsManager.on(ReelsEvents.ADD_FAKE_SCATTER, (scatterData: FakeScatterData) => {
            const scatterSymbol = this.fakeScatters[scatterData.reelIndex];
            if (
                DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN &&
                scatterData.reelIndex === reelsConfig.reelsNumber - 1
            ) {
                scatterSymbol.getMainSpine().setSkinByName(`sfs`);
                scatterSymbol.getUpgradeAnimation();
            }
            scatterSymbol.alpha = 0;

            gsap.to(scatterSymbol, {
                alpha: 1,
                duration: 0.35,
                delay: 0.25,
                onStart: () => {
                    this.addChild(scatterSymbol);
                },
            });
        });

        EventsManager.on(
            ReelsEvents.UPDATE_FAKE_SCATTER_POSITION,
            (scatterData: FakeScatterData) => {
                const globalPosition = scatterData.symbolGlobalPosition!;
                const localPosition = this.toLocal(globalPosition);
                const scatterSymbol = this.fakeScatters[scatterData.reelIndex];
                scatterSymbol.position.set(localPosition.x, localPosition.y);
            }
        );

        EventsManager.on(ReelsEvents.REMOVE_FAKE_SCATTERS, () => {
            this.fakeScatters.map((scatter) => {
                if (scatter.isWild) {
                    gsap.to(scatter.getMainSpine(), {
                        alpha: 0,
                        duration: 0.2,
                        onComplete: () => {
                            this.removeChild(scatter);
                            scatter.alpha = 1;
                            scatter.reset();
                        },
                    });
                } else {
                    gsap.to(scatter, {
                        alpha: 0,
                        duration: 0.2,
                        onComplete: () => {
                            this.removeChild(scatter);
                            scatter.alpha = 1;
                            scatter.reset();
                        },
                    });
                }
            });
        });

        EventsManager.on(ReelsEvents.RESET_FAKE_SCATTERS, () => {
            this.fakeScatters.map((scatter) => {
                const initialPosition = { x: scatter.x, y: scatter.y };
                scatter.reset();
                scatter.position.set(initialPosition.x, initialPosition.y);
            });
        });

        EventsManager.on(GameEvents.UPDATE_GLOBAL_MULTIPLIER, (multiplier: number) => {
            this.generalMultiplier.alpha = 1;
            const currentMultiplier = +this.generalMultiplier.text.split(`x`)[1];
            this.generalMultiplier.updateValue(`x${currentMultiplier + multiplier}`);

            if (currentMultiplier + multiplier > 0) {
                this.generalMultiplier.alpha = 1;
            } else {
                this.generalMultiplier.alpha = 0;
            }
        });

        EventsManager.on(GameEvents.RESPIN_FRAME, (show: boolean) => {
            const data = DDD.model.data.isRestoreState ? DDD.model.initData : DDD.model.gameData;
            if (data.mode === AbstractGameMode.NORMAL || data.mode === AbstractGameMode.RESPIN)
                if (!this.isRespinFrame && show) {
                    this.respinFrame.getSpineTween(`respin`);
                    this.isRespinFrame = true;
                } else if (this.isRespinFrame && !show) {
                    this.respinFrame.getSpineTween(`fade_out`);
                    this.isRespinFrame = false;
                }
        });
    }

    protected initGraphics(): void {
        this.reelsContainer = new Container();
        this.reelsContainer.name = `ReelsContainer`;
        this.reelsContainer.position.set(reelsConfig.reelsContainerX, reelsConfig.reelsContainerY);

        this.addChild(this.reelsContainer);
        this.addReelsMask();

        this.upperReelsMask = generateScaledSprite(`DDD/upperMask`);
        this.upperReelsMask.scale.set(1);
        this.upperReelsMask.position.set(-5, 70);
        this.addChild(this.upperReelsMask);

        this.lowerReelsMask = generateScaledSprite(`DDD/lowerMask`);
        this.lowerReelsMask.scale.set(1);
        this.lowerReelsMask.position.set(-5, 630);
        this.addChild(this.lowerReelsMask);

        this.reelsGridSprite = Sprite.from(`DDD/reel_grid`);
        this.reelsGridSprite.name = `grid`;
        this.reelsGridSprite.x = -2;
        this.reelsGridSprite.y = 66;
        this.addChild(this.reelsGridSprite);

        this.generalMultiplier = new AbstractBitmapText({
            text: `x${0}`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: generalMultiplier,
                // letterSpacing: 20,
                tint: 0xcccccc,
            },
        });
        this.generalMultiplier.name = `generalMultiplier`;
        this.generalMultiplier.pivot.y = 50;
        this.generalMultiplier.position.set(635, 55);
        this.generalMultiplier.alpha = 0;
        this.addChild(this.generalMultiplier);

        this.respinFrame = new AbstractSpine(DDDSpineNames.RESPIN_FRAME);
        this.respinFrame.position.set(360, 385);
        this.respinFrame.name = `respinFrame`;
        this.addChild(this.respinFrame);

        if (devDebugConfig.showReelDebugGraphics) {
            if (devDebugConfig.disableReelMask) {
                this.scale.set(0.6, 0.6);
                this.position.set(184, 300);
            } else {
                this.scale.set(1, 1);
                this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
            }
        } else {
            this.scale.set(1, 1);
            this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
        }

        this.logoSprite = generateScaledSprite(`DDD/logo`);
        this.logoSprite.name = `landscapeLogo`;
        this.logoSprite.scale.set(0.28);
        this.logoSprite.anchor.set(0.5, 0.5);

        this.checkLogoPosition();
    }

    public onResize(): void {
        this.checkLogoPosition();
    }

    private checkLogoPosition(): void {
        if (!NolimitApplication.isLandscape) {
            this.logoSprite.position.set(350, 0);
            this.logoSprite.scale.set(0.32);
        } else {
            this.logoSprite.position.set(350, 40);
            this.logoSprite.scale.set(0.28);
        }

        this.addChild(this.logoSprite);
    }

    protected addReelsMask() {
        this.reelsContainerMask = new Container();
        this.reelsContainerMask.name = `ReelsContainerMask`;

        if (devDebugConfig.disableReelMask) {
            return;
        }

        const reelsContainerMaskGraphics: Graphics = new Graphics();
        reelsContainerMaskGraphics.beginFill(0x00ff00, 0.5);
        reelsContainerMaskGraphics.drawRect(-245, 0, 1200, 562);
        reelsContainerMaskGraphics.endFill();

        this.reelsContainerMask.position.set(-10, reelsConfig.reelsContainerY - 2);

        this.reelsContainerMask.addChild(reelsContainerMaskGraphics);

        this.reelsContainer.mask = this.reelsContainerMask;

        this.addChild(this.reelsContainerMask);
    }

    protected onGameModeUpdate(): void {
        this.currentReels.forEach((reel) => {
            reel.gameMode = this.gameMode;
        });
    }

    public generateInitialReels(symbols: string[][]): void {
        //this is pre rendering the symbols to avoid the first frame lag
        const pregeneratedSymbols: AbstractSymbol[] = [];

        for (const key in DDDSymbolNames) {
            if (Object.prototype.hasOwnProperty.call(DDDSymbolNames, key)) {
                const value = DDDSymbolNames[key as keyof typeof DDDSymbolNames];
                for (let i = 0; i < 3; i++) {
                    const symbol = getSymbolFromPool(value);
                    pregeneratedSymbols.push(symbol);
                }
            }
        }

        pregeneratedSymbols.forEach((symbol) => returnSymbolToPool(symbol));

        symbols.forEach((reel: string[], index) => {
            const gameReel = new DDDReel(index, reel);
            gameReel.gameMode = this.gameMode;
            gameReel.x =
                (symbolConfig.symbolWidth + reelsConfig.reelSpacing) * gameReel.reelId +
                reelsConfig.reelSpacingOffset[gameReel.reelId];

            this.currentReels.push(gameReel);

            this.reelsContainer.addChild(gameReel);
        });
    }

    public getReels(): AbstractReel[] {
        return this.currentReels;
    }

    protected override initExtra() {
        super.initExtra();

        this.fakeScatters = [];
        for (let i = 0; i < reelsConfig.reelsNumber; i++) {
            this.fakeScatters.push(new DDDScatterSymbol());
        }
    }
}
